import React from 'react';
import {
  Tab as RACTab,
  TabList as RACTabList,
  TabPanel as RACTabPanel,
  type TabsProps as RACTabProps,
  Tabs as RACTabs,
  type TabListProps,
  type TabPanelProps,
  type TabProps,
  composeRenderProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { composeTailwindRenderProps, focusRingStyle } from './utils';

const TabsContext = React.createContext<{
  variant: 'underline' | 'pills' | 'segment';
  orientation: 'vertical' | 'horizontal';
}>({
  variant: 'underline',
  orientation: 'horizontal',
});

export type TabsProps = RACTabProps &
  ({ variant?: 'underline' | 'pills' } | { variant: 'segment'; orientation?: never });

export function Tabs({
  variant = 'underline',
  orientation = 'horizontal',
  keyboardActivation = 'manual',
  ...props
}: TabsProps) {
  return (
    <TabsContext.Provider value={{ variant, orientation }}>
      <RACTabs
        {...props}
        keyboardActivation={keyboardActivation}
        orientation={orientation}
        className={composeTailwindRenderProps(props.className, [
          'group flex',
          orientation === 'horizontal' ? 'flex-col' : 'flex-row',
        ])}
      />
    </TabsContext.Provider>
  );
}

const tabList = {
  base: {
    horizontal: ['whitespace-nowrap'],
    vertical: ['flex-col'],
  },
  underline: {
    horizontal: ['w-full space-x-4 border-b'],
    vertical: ['space-y-3.5 self-start border-l'],
  },
  pills: {
    horizontal: ['space-x-4'],
    vertical: ['space-y-2'],
  },
  segment: {
    horizontal: [
      'gap-x-4',
      'rounded-sm',
      'bg-solid-quiet',
      'p-1',
      'roundednc-600/45',
      'dark:border',
      'dark:border-border',
    ],
    vertical: [],
  },
};

export function TabList<T extends object & { title: string }>(props: TabListProps<T>) {
  const { variant, orientation } = React.useContext(TabsContext);

  return (
    <div className="flex overflow-x-auto pb-px pl-px">
      <RACTabList
        {...props}
        className={composeTailwindRenderProps(props.className, [
          'flex',
          'text-base/6 sm:text-sm/6',
          tabList.base[orientation].join(' '),
          tabList[variant][orientation].join(' '),
        ])}
      />
    </div>
  );
}

const tabPanel = {
  underline: {
    horizontal: ['py-4'],
    vertical: ['px-4'],
  },
  pills: {
    horizontal: ['px-5 py-4'],
    vertical: ['pl-8'],
  },
  segment: {
    horizontal: ['px-3 py-4'],
    vertical: [],
  },
};

export function TabPanel(props: TabPanelProps) {
  const { variant, orientation } = React.useContext(TabsContext);

  return (
    <RACTabPanel
      {...props}
      className={composeTailwindRenderProps(props.className, [
        'flex-1 outline-0',
        tabPanel[variant][orientation].join(' '),
      ])}
    />
  );
}

const tab = {
  underline: {
    base: ['before:absolute', 'before:bg-accent'],
    horizontal: [
      'before:bottom-[-1.5px]',
      'before:w-full',
      'before:inset-x-0',
      'selected:before:h-[2px]',
      'p-2',
    ],
    vertical: [
      'before:inset-y-0',
      'selected:before:bg-accent',
      'selected:before:left-[-1.5px]',
      'selected:before:w-[2px]',
      'px-4',
    ],
  },
  pills: {
    base: [
      'flex',
      'items-center',
      'px-3',
      'py-2',
      'rounded-sm',
      'selected:bg-accent',
      'selected:text-white',
    ],
    horizontal: [],
    vertical: [],
  },
  segment: {
    base: [
      'flex-1',
      'justify-center',
      'px-6',
      'py-1',
      'selected:bg-background',
      'selected:text-foreground',
      'selected:shadow-xs',
      'selected:rounded-[calc(var(--radius-lg)-2px)]',
      '[&>[data-ui=icon]:not([class*=size-])]:size-4',
    ],
    horizontal: [],
    vertical: [],
  },
};

export function Tab(props: TabProps) {
  const { variant, orientation } = React.useContext(TabsContext);

  return (
    <RACTab
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) => {
        return twMerge(
          'relative flex items-center gap-x-3 rounded-sm font-medium outline-hidden outline-0',
          // disable
          'disabled:opacity-50',

          '[&>[data-ui=icon]:not([class*=size-])]:size-[1.125rem]',

          // hover
          '[&:not([data-selected])]:text-muted',
          '[&:not([data-selected])]:hover:text-foreground',

          tab[variant].base,
          tab[variant][orientation],

          renderProps.isFocusVisible && [focusRingStyle, 'ring-2'],
          className,
        );
      })}
    />
  );
}
